/**
 * Yorùbá.
 *
 * First pass. Awaiting review by a native speaker with clinical vocabulary —
 * `reviewed: false` is deliberate and should gate any clinical release.
 *
 * Two translation notes worth recording, because a reviewer will want to argue
 * with them:
 *
 *   • "Ori" is left untouched throughout. It is the product name and it is
 *     already a Yorùbá word (orí — head, and the inner self that carries a
 *     person's destiny). Naming the thing that reads your face after the head
 *     is the whole point of the name, and translating it would erase it.
 *
 *   • "Ìsọwọ́n" is used for a measurement or reading. Where a more everyday
 *     phrase exists ("bí ọkàn ṣe ń lù" — how the heart is beating), it is
 *     preferred over clinical register, because this screen is read by people
 *     deciding whether to trust us, not by clinicians.
 */

export default {
  code: 'yo',
  intl: 'yo-NG',
  name: 'Yoruba',
  endonym: 'Yorùbá',
  reviewed: false,
  strings: {
    'app.tagline': 'Ó ń ka bí ọkàn rẹ ṣe ń lù láti inú kámẹ́rà — ó sì máa sọ òtítọ́ tí kò bá lè rí i.',
    'app.disclaimer': 'Àpẹẹrẹ iṣẹ́. Kì í ṣe ohun èlò ìṣègùn, a kò tíì fọwọ́ sí i, kì í sì í ṣe àyẹ̀wò àìsàn.',
    'app.skip': 'Fò sí àkóónú pàtàkì',
    'app.language': 'Èdè',
    'app.install': 'Fi Ori sínú ẹ̀rọ yìí',
    'app.update.title': 'Ẹ̀dà tuntun ti Ori ti ṣetán',
    'app.update.body':
      'A ó lò ó nígbà tí o bá tún ṣí ohun èlò náà, tàbí kí o yí padà báyìí. Kò sí ohun tí o ti fi pamọ́ tí yóò kán.',
    'app.update.action': 'Ṣe àtúnṣe báyìí',
    'app.queued': 'Ìsọwọ́n {count} ń dúró láti fi ránṣẹ́',
    'app.queued.one': 'Ìsọwọ́n 1 ń dúró láti fi ránṣẹ́',

    'nav.start': 'Kí ni èyí',
    'nav.scan': 'Ṣe ìsọwọ́n',
    'nav.ori': 'Ori rẹ',
    'nav.record': 'Àkọsílẹ̀ rẹ',
    'nav.consent': 'Ohun tí o ń pín',
    'nav.care': 'Ibi tí o lè lọ',
    'nav.pledge': 'Ìlérí Awọ̀ Ara',

    'status.deviceKey': 'Àkọsílẹ̀ wà nínú ẹ̀rọ yìí',
    'status.noKey': 'Kò sí kọ́kọ́rọ́ ẹ̀rọ',
    'status.preparingKey': 'À ń pèsè kọ́kọ́rọ́ ẹ̀rọ…',
    'status.corpusLive': 'Àpapọ̀ ìsọwọ́n · ó ń ṣiṣẹ́',
    'status.corpusOffline': 'Àpapọ̀ ìsọwọ́n · kò sí ìsopọ̀',
    'status.corpusUnset': 'A kò tíì ṣètò àpapọ̀ ìsọwọ́n',
    'status.sharing': 'Ó ń pín ìsọwọ́n',
    'status.notSharing': 'Kò pín',

    'start.eyebrow': 'OmniAlera Ori',
    'start.title.line1': 'Ìlù ọkàn rẹ,',
    'start.title.line2': 'tí ìmọ́lẹ̀ ń kà',
    'start.plain1': 'Ọkàn rẹ ń ti ẹ̀jẹ̀ gba ojú rẹ kọjá. Ìgbà kọ̀ọ̀kan tí ó bá tì, awọ̀ ara rẹ máa pọ́n díẹ̀.',
    'start.plain2': 'Ó kéré jù fún ojú rẹ láti rí. Ṣùgbọ́n kámẹ́rà lè rí i.',
    'start.plain3': 'Nítorí náà o wo kámẹ́rà fún ìṣẹ́jú-àáyá díẹ̀, Ori sì ka ìlù ọkàn rẹ.',
    'start.plain3b': 'Kò sí aago. Kò sí okùn. Kò sí ohun tí o máa rà.',
    'start.problem': 'Ìṣòro náà nìyí',
    'start.step1.lead': 'Awọ̀ ara ní àwọ̀.',
    'start.step1.body': 'Awọ̀ dúdú ń gba ìmọ́lẹ̀ tí kámẹ́rà ń wá sí i, nítorí náà ìmọ̀ràn náà máa kéré.',
    'start.step2.lead': 'Ọ̀pọ̀ ohun èlò kò kà á sí.',
    'start.step2.body': 'Wọ́n máa fi nọ́mbà hàn ọ́ lọ́nàkọnà. Ó lè jẹ́ àṣìṣe ńlá, síbẹ̀ ó dàbí ẹni tí ó dájú pátápátá.',
    'start.step3.lead': 'Ori kì í gbèsè.',
    'start.step3.body': 'Tí kò bá lè rí ìlù ọkàn rẹ kedere, ó máa sọ fún ọ. Ìyẹn ni gbogbo ìyàtọ̀ náà.',
    'start.step4.lead': 'O lè ràn wá lọ́wọ́ láti tún un ṣe.',
    'start.step4.body': 'Gbogbo ìsọwọ́n tí o bá yàn láti pín — kódà èyí tí ó kùnà — ń kọ́ ẹ̀dà tí ń bọ̀ láti rí awọ̀ bí tirẹ. A ó san ọ́, o sì lè gbà á padà nígbàkúùgbà.',
    'start.before.title': 'Kí o tó ṣe ìsọwọ́n àkọ́kọ́',
    'start.before.body': 'Yan àpótí tí ó sún mọ́ awọ̀ ara rẹ jùlọ. Ọ̀pọ̀ ohun èlò ìlera kì í bèèrè rárá. Òun ni ohun kan ṣoṣo tí ó pinnu bóyá ìwọ̀n náà yóò ṣiṣẹ́ rárá.',
    'start.pledge.title': 'Ṣe Ìlérí Awọ̀ Ara',
    'start.pledge.body': 'Jẹ́ kí ìsọwọ́n kọ̀ọ̀kan — láìsí ohunkóhun tí ó dá ọ mọ̀ — dara pọ̀ mọ́ àkójọpọ̀ ìsọwọ́n tí ń kọ́ àwọn ẹ̀rọ wọ̀nyí láti rí awọ̀ dúdú. O lè wo bí nọ́mbà náà ṣe ń lọ, o sì lè pa á nígbàkúùgbà.',
    'start.pledge.earn': 'Ó ń jèrè kírẹ́dítì 25 fún ìsọwọ́n kọ̀ọ̀kan',
    'start.cta.ready': 'Ṣe ìsọwọ́n àkọ́kọ́ mi',
    'start.cta.needTone': 'Yan awọ̀ ara ní àkọ́kọ́',
    'start.cta.counter': 'Wo nọ́mbà náà ní àkọ́kọ́',
    'tone.lightest': 'Fúyẹ́ jùlọ',
    'tone.darkest': 'Dúdú jùlọ',
    'tone.chosen': 'Awọ̀ {n} nínú 10',
    'tone.none': 'A kò tíì yàn',
    'tone.label': 'Awọ̀ ara {n} nínú 10',
    'tone.group': 'Awọ̀ ara rẹ',

    'scan.eyebrow': 'Ṣe ìsọwọ́n',
    'scan.title.idle': 'Dúró jẹ́ẹ́ kí o sì wo kámẹ́rà',
    'scan.title.running': 'À ń ka ọ́ báyìí — dúró jẹ́ẹ́',
    'scan.title.locked': 'A ti rí i',
    'scan.title.failed': 'Ori kò lè ka ọ́',
    'scan.intro':
      'Ori ń wo apá mẹ́ta ojú rẹ lẹ́ẹ̀kan náà — iwájú orí àti ẹ̀rẹ̀kẹ́ méjèèjì — ó sì ka ọ̀kọ̀ọ̀kan ní ọ̀nà méjì ọ̀tọ̀ọ̀tọ̀. Ó máa fún ọ ní nọ́mbà kìkì nígbà tí gbogbo wọn bá fara mọ́ ara wọn.',
    'scan.start': 'Bẹ̀rẹ̀',
    'scan.running': 'Ó ń ka…',
    'scan.again': 'Tún un ṣe',
    'scan.useCamera': 'Lo kámẹ́rà mi',
    'scan.useSimulated': 'Padà sí àpẹẹrẹ',
    'scan.privacy':
      'Kámẹ́rà tààrà, ní àkókò gidi. Kò sí ohun tí o rí tí ó kúrò nínú ẹ̀rọ yìí — ìwọ̀n náà ń ṣẹlẹ̀ nínú aṣàwákiri rẹ, a kò sì gbé àwòrán kankan sókè rí.',
    'scan.simulated':
      'Àpẹẹrẹ ènìyàn ní awọ̀ {tone}, tí a ṣẹ̀dá ní ìlọ́po mẹ́rin yára ju àkókò gidi lọ kí o má bàa dúró. Nǹkan bí ìdá {attenuation}% ìmọ̀ràn ìlù ọkàn ló ń yè ní awọ̀ yìí.',
    'scan.skipped': 'A fo àwòrán {count} — Ori fẹ́ dúró ju kí ó máa gbèsè.',
    'scan.skipped.one': 'A fo àwòrán 1 — Ori fẹ́ dúró ju kí ó máa gbèsè.',
    'scan.guess': 'Ìgbèsè tí ó dára jùlọ báyìí: {bpm} bpm — a kò tíì gbà á gbọ́.',
    'scan.guess.disagree': 'Àwọn ọ̀nà méjèèjì kò fara mọ́, nítorí náà Ori ṣì ń wá.',
    'scan.guess.settling': 'À ń dúró kí ó balẹ̀.',

    'stat.elapsed': 'Àkókò',
    'stat.clarity': 'Ìmọ́lẹ̀',
    'stat.agreement': 'Ìfarabalẹ̀ apá',
    'stat.pulse': 'Ìlù ọkàn',
    'stat.breathing': 'Ìmí',
    'stat.variability': 'Ìyàtọ̀',
    'unit.bpm': 'bpm',
    'unit.perMin': '/ìṣẹ́jú',
    'unit.ms': 'ms',
    'unit.s': 'ìṣ',

    'advice.ok': 'Ó dára — dúró jẹ́ẹ́.',
    'advice.no_face': 'A kò rí ojú — mú ara rẹ wá sí àárín.',
    'advice.too_dark': 'Ó ṣú jù. Kọjú sí fèrèsé tàbí àtùpà.',
    'advice.dim': 'Ó ṣú díẹ̀ — ìmọ́lẹ̀ sí i yóò mú kí ó yára.',
    'advice.clipped': 'Ìmọ́lẹ̀ pọ̀ jù — yí padà kúrò nínú ìmọ́lẹ̀.',
    'advice.bright': 'Ó mọ́lẹ̀ gan-an. Gbìyànjú láti yí díẹ̀.',
    'advice.motion': 'Ìṣíkiri pọ̀ jù — dúró jẹ́ẹ́.',
    'advice.slight_motion': 'Gbìyànjú láti dúró jẹ́ẹ́.',

    'card.share': 'Pín ìsọwọ́n yìí',
    'card.sharing': 'À ń pèsè…',
    'card.downloaded': 'A ti fi pamọ́ sínú ẹ̀rọ rẹ.',
    'card.failed': 'A kò lè ṣe káàdì náà nínú ẹ̀rọ yìí.',
    'card.pulse': 'Ìlù ọkàn',
    'card.breathing': 'Ìmí',
    'card.variability': 'Ìyàtọ̀',
    'card.time': 'Àkókò tí ó gbà',
    'card.regions': 'Apá tí ó fara mọ́',
    'card.methods': 'Ìyàtọ̀ ọ̀nà',
    'card.tone': 'Awọ̀ ara',
    'card.trusted': 'A fọwọ́ sí i láti apá mẹ́ta àti ọ̀nà méjì',
    'card.honest': 'Ori sọ ọ́ dípò kí ó gbèsè',
    'card.refusedLabel': 'Kò sí ìsọwọ́n',
    'card.refusedHeadline': 'Ori kò lè rí mi kedere.',
    'card.lockedBody': 'A wọ̀n ọ́ láti inú ìmọ́lẹ̀ tí ó tàn padà láti awọ̀ ara mi — kò sí aago, kò sí okùn, kò sí ohun tí ó fọwọ́ kàn mí. Ori máa sọ nọ́mbà kìkì nígbà tí àwọn apá ọ̀tọ̀ọ̀tọ̀ àti ọ̀nà ọ̀tọ̀ọ̀tọ̀ bá fara mọ́.',
    'card.refusedBody': 'Dípò kí ó tẹ nọ́mbà àṣìṣe pẹ̀lú ìdánilójú, Ori kọ̀ — ó sì kọ ìkọ̀ náà sílẹ̀, kí a lè wọn àṣìṣe náà kí a sì tún un ṣe dípò kí ó máa tún ara rẹ̀ ṣe ní ìdákẹ́jẹ́ẹ́.',
    'card.disclaimer': 'Àpẹẹrẹ. Kì í ṣe ohun èlò ìṣègùn. Kì í ṣe àyẹ̀wò àìsàn.',

    'canvas.traceEmpty': 'ìmọ́lẹ̀ tí ó ń padà láti awọ̀ ara rẹ',
    'canvas.signalReaches': 'ìdá {percent}% ìmọ̀ràn ìlù ọkàn ló dé ọ̀dọ̀ kámẹ́rà',
    'canvas.noReading': 'kò sí ìsọwọ́n síbẹ̀',
    'canvas.steady': '{bpm} bpm · ó dúró ṣinṣin',
    'canvas.couldNotRead': 'kò lè ka ọ́',

    'result.title': 'Ìsọwọ́n rẹ',
    'result.found': 'a rí i ní ìṣẹ́jú-àáyá {seconds}',
    'result.none': 'kò sí ìmọ̀ràn tó ṣe kedere',
    'result.confirmed':
      'A fọwọ́ sí i láti inú ìsọwọ́n apá {regions} àti ọ̀nà ọ̀tọ̀ọ̀tọ̀ {methods}, tí wọ́n fara mọ́ ara wọn láàrin {spread} bpm.',
    'result.refused':
      'Ori wo fún gbogbo àkókò náà, kò sì rí ìlù ọkàn tí ó gbẹ́kẹ̀lé. Ní awọ̀ ara yìí, nǹkan bí ìdá {attenuation}% ìmọ̀ràn ìlù ọkàn nìkan ló ń dé ọ̀dọ̀ kámẹ́rà. Dípò kí ó fún ọ ní nọ́mbà tí kò lè dúró lẹ́yìn rẹ̀, Ori ń sọ fún ọ pé kò lè rí ọ.',
    'result.conventional': 'Ohun èlò àtijọ́ ìbá ti sọ pé {bpm} bpm láti inú ìsọwọ́n kan náà',
    'result.conventional.locked': '.',
    'result.conventional.failed': ', láìsí ìkìlọ̀ kankan. Èyí ni ohun tí a ń tà lónìí.',

    'diag.toggle': 'Ohun tí Ori ń rí',
    'diag.empty': 'Kò sí nǹkan síbẹ̀. Ori nílò nǹkan bí ìṣẹ́jú-àáyá mẹ́jọ fídíò tó dára kí ó tó ní ohun tí ó lè fihàn.',
    'diag.method': 'Ọ̀nà',
    'diag.reads': 'Ó ka',
    'diag.from': 'Láti',
    'diag.method.pos': 'Àwọ̀ (POS)',
    'diag.method.green': 'Ìkànnì àwọ̀ ewé',
    'diag.regions': 'Apá {count}',
    'diag.regions.one': 'Apá 1',
    'diag.disagree':
      'Àwọn ọ̀nà méjèèjì yàtọ̀ ní {spread} bpm. Ó ṣeé ṣe kí ọ̀kan nínú wọn máa tẹ̀lé ìṣíkiri dípò ìlù ọkàn, nítorí náà Ori kò ní sọ ọ̀kankan.',
    'diag.agree': 'Àwọn ọ̀nà méjèèjì fara mọ́ ara wọn láàrin {spread} bpm.',
    'diag.framesUsed': 'Àwòrán tí a lò',
    'diag.harmonic': 'Ìtìlẹ́yìn harmonic',
    'diag.spread': 'Ìyàtọ̀ láàrin apá',
    'diag.perfusion': 'Ìṣàn ẹ̀jẹ̀',

    'consent.eyebrow': 'Ohun tí o ń pín',
    'consent.title.lead': 'Àṣàyàn mẹ́rin.',
    'consent.title.em': 'Gbogbo wọn kò ṣiṣẹ́ àyàfi tí o bá sọ.',
    'consent.lede': 'O lè pa ọ̀kọ̀ọ̀kan wọn láìpàdánù àkọsílẹ̀ rẹ, ọ̀kọ̀ọ̀kan sì ń san ọ́ nígbà tí ó bá ń ṣiṣẹ́.',
    'consent.offline.title': 'Kò sí ìsopọ̀',
    'consent.offline.body': 'A kò lè dé ọ̀dọ̀ iṣẹ́ Ori, nítorí náà a kò lè yí àwọn àṣàyàn wọ̀nyí padà báyìí. Àkọsílẹ̀ rẹ wà láìléwu nínú ẹ̀rọ yìí, a kò sì ń pín nǹkankan nígbà tí kò sí ìsopọ̀.',
    'consent.error.title': 'Kò fi pamọ́',
    'consent.corpus.title': 'Ràn wá lọ́wọ́ láti tún ẹ̀rọ náà ṣe',
    'consent.corpus.body': 'Ìsọwọ́n kọ̀ọ̀kan, láìsí ohunkóhun tí ó dá ọ mọ̀, ń dara pọ̀ mọ́ àkójọpọ̀ ìsọwọ́n tí ń kọ́ àwọn ẹ̀rọ wọ̀nyí láti rí awọ̀ dúdú. Ìsọwọ́n tí ó kùnà ni ó ṣe pàtàkì jùlọ.',
    'consent.corpus.earn': 'Kírẹ́dítì 25 fún ìsọwọ́n kọ̀ọ̀kan',
    'consent.routing.title': 'Sọ ibi tí mo lè lọ',
    'consent.routing.body': 'Jẹ́ kí Ori tọ́ ọ sí pẹpẹ OmniAlera tí ó tọ́ nígbà tí ìsọwọ́n bá tọ́ sí i. A kò fi nǹkankan ránṣẹ́ sí ibikíbi — a ń ṣe ìmọ̀ràn náà nínú ẹ̀rọ rẹ.',
    'consent.routing.earn': 'Ọ̀fẹ́, kò sì sí ìsọwọ́n tí ó kúrò',
    'consent.cohort.title': 'Dara pọ̀ mọ́ ẹgbẹ́ ìwádìí',
    'consent.cohort.body': 'Jẹ́ apá kan ẹgbẹ́ ìgbà pípẹ́ tí a kò dá mọ̀, tí àwọn alábàáṣiṣẹ́pọ̀ ìwádìí tí a ti yẹ̀wò lè kẹ́kọ̀ọ́ nípa rẹ̀. Kúrò nígbàkúùgbà: kíkúrò ń yọ ọ́ kúrò nínú gbogbo àyọkà ọjọ́ iwájú, a ó sì sọ fún àwọn alábàáṣiṣẹ́pọ̀ tí wọ́n ní àyọkà àtijọ́.',
    'consent.cohort.earn': 'Kírẹ́dítì 10 sí i fún ìsọwọ́n kọ̀ọ̀kan',
    'consent.assistant.title': 'Jẹ́ kí Ori ka àkọsílẹ̀ mi',
    'consent.assistant.body': 'Gba àkópọ̀ ìsọwọ́n rẹ láàyè láti bá ìbéèrè rìn kí Ori lè dáhùn nípa àṣà tirẹ. Orúkọ rẹ kì í ṣe apá kan rẹ̀ láé.',
    'consent.assistant.earn': 'Ọ̀fẹ́',
    'consent.earned.title': 'Ohun tí o ti jèrè',
    'consent.earned.value': 'Kírẹ́dítì {credits}',
    'consent.earned.body': 'Iye àpẹẹrẹ ni. Nínú iṣẹ́ gidi, wọ́n máa di owó lórí fóònù. Kókó náà ni pé a ń san owó fún ìpèsè, kì í ṣe ẹ̀bùn fún ilé-iṣẹ́ tí yóò tà á.',
    'consent.withdraw.title': 'Bí yíyọ kúrò ṣe ń ṣiṣẹ́ ní ti gidi',
    'consent.withdraw.body1': 'Pípa ẹgbẹ́ ìwádìí ń ṣe nǹkan mẹ́ta, kì í ṣe ọ̀kan. A yọ ọ́ kúrò nínú gbogbo àyọkà tí a bá ṣe láti ìsinsìnyí lọ. Àyọkà tí a ti ṣe ṣùgbọ́n tí a kò tíì gbà máa yọ àwọn ìlà rẹ kúrò kí a tó fi lé wọn lọ́wọ́. Fún àwọn àyọkà tí a ti fi lélẹ̀, a ó fi ìwé ránṣẹ́ sí alábàáṣiṣẹ́pọ̀ tí ó ní wọn pé o ti yọ ara rẹ kúrò.',
    'consent.withdraw.body2': 'Ìkẹta yẹn ni apá tí ó fẹ́rẹ̀ẹ́ jẹ́ pé kò sí ẹni tí ó ń kọ́. Ìfọwọ́sí tí kò lè dé ọ̀dọ̀ ìsọwọ́n tí ó ti kúrò nílé kì í ṣe ìfọwọ́sí — ìwé lásán ni.',

    'shared.why': 'Ìdí tí èyí fi wà',
    'shared.kept': 'A pa á mọ́ nínú ẹ̀rọ yìí. Pípín kò ṣiṣẹ́.',
    'shared.keptNoService': 'A pa á mọ́ nínú ẹ̀rọ yìí — a kò ṣètò iṣẹ́ àpapọ̀ kankan.',
    'shared.sending': 'Ó ń pín…',
    'shared.added': 'A ti fi kún àpapọ̀.',
    'shared.addedWithQueue': 'A ti fi kún àpapọ̀, pẹ̀lú ìsọwọ́n {count} tí ó ti ń dúró.',
    'shared.queuedOffline':
      'A pa á mọ́ nínú ẹ̀rọ yìí. A ó pín in gbàrà tí o bá padà sí orí ìtàkùn ({count} ń dúró).',
    'shared.refused': 'Iṣẹ́ náà kò gba ìsọwọ́n yìí, nítorí náà a kò pín nǹkankan.',
    'shared.flushed': 'Ìsọwọ́n {count} tí ó ti ń dúró ti wà nínú àpapọ̀ báyìí.',
    'shared.flushed.one': 'Ìsọwọ́n 1 tí ó ti ń dúró ti wà nínú àpapọ̀ báyìí.',

    /* ── ojú-ìwòye méjì ─────────────────────────────────────────────── */
    'view.group': 'Ta ni ìsọwọ́n yìí wà fún',
    'view.patient': 'Fún mi',
    'view.clinician': 'Fún oníṣègùn',

    'floor.empty': 'Kò sí ohun tí a lè yà síbẹ̀.',
    'floor.bar': 'ohun tí ó gbọ́dọ̀ ré kọjá',
    'floor.axis': 'àwọn ìwọ̀n tí ó ṣeéṣe',
    'floor.alt': 'Gbogbo ìwọ̀n tí Ori gbé yẹ̀wò, àti ìpele tí ó gbọ́dọ̀ dé kí Ori tó lè sọ ọ́.',
    'floor.caption.locked':
      'Àmì gíga náà ni bí ọkàn rẹ ṣe ń lù. Ó ré kọjá ìlà àmì náà dáadáa, èyí tí ó jẹ́ ìpele tí Ori béèrè kí ó tó sọ ohunkóhun.',
    'floor.caption.refused':
      'Kò sí ohun tí ó dé ìlà àmì náà níbí. Ìlà yẹn ni ìpele tí Ori béèrè kí ó tó sọ ìwọ̀n kan — nítorí náà kò sọ ọ̀kan.',

    'clin.locked': 'Ó dì mú ní {bpm} bpm lẹ́yìn ìṣẹ́jú-àáyá {seconds}. Gbogbo ìdí márùn-ún ni ó tẹ́ ẹ lọ́rùn.',
    'clin.refused': 'Kò sí ìsọwọ́n. Ó kùnà lórí: {gates}.',
    'clin.refusedNoGate':
      'Kò sí ìsọwọ́n. Àkókò náà parí kí àyẹ̀wò kankan tó tẹ́ gbogbo ìdí márùn-ún lọ́rùn.',
    'clin.region': 'Agbègbè',
    'clin.region.forehead': 'Iwájú orí',
    'clin.region.leftCheek': 'Ẹ̀rẹ̀kẹ́ òsì',
    'clin.region.rightCheek': 'Ẹ̀rẹ̀kẹ́ ọ̀tún',
    'clin.region.other': 'Agbègbè {n}',
    'clin.attenuation': 'Àmì tí ó dé ẹ̀rọ ìmọ̀lára',
    'clin.engine': 'Ẹ̀rọ ìwádìí',
    'clin.conventional':
      'Ẹ̀rọ ìdíwọ̀n tí ó wọ́pọ̀ ìbá ti sọ {bpm} bpm láti inú ìgbàsílẹ̀ kan náà yìí.',
    'clin.disclaimer':
      'Àpẹẹrẹ iṣẹ́. Kì í ṣe ohun èlò ìṣègùn, a kò fi wé ohun èlò ìdíwọ̀n tòótọ́, kì í sì í ṣe ìpìlẹ̀ fún ìpinnu ìṣègùn kankan.',

    'gate.header.criterion': 'Ìdí',
    'gate.header.measured': 'Ohun tí a wọ̀n',
    'gate.header.required': 'Ohun tí a béèrè',
    'gate.prominence': 'Gíga àmì náà',
    'gate.harmonic': 'Ìtìlẹ́yìn ìlù kejì',
    'gate.stability': 'Ìdúróṣinṣin',
    'gate.agreement': 'Ìfohùnṣọ̀kan agbègbè',
    'gate.methods': 'Ìyàtọ̀ láàrin ọ̀nà',
    'gate.pass': 'ó kọjá',
    'gate.fail': 'ó kùnà',
    'gate.skip': 'a kò yẹ̀ ẹ́ wò',
    'gate.notAssessed': 'a kò lè yẹ̀ ẹ́ wò — agbègbè {regions} ni ó ṣeé lò',


    /* ── ohun tí o lè retí ní ìwọ̀n awọ tí o yàn ─────────────────────── */
    'tone.expect.strong':
      'Ní ìwọ̀n awọ yìí àmì náà lágbára. Ori yóò lè ka ọ́ ní ọ̀pọ̀ ìgbà.',
    'tone.expect.good':
      'Ní ìwọ̀n awọ yìí àmì náà dára. Ori yóò sábà lè ka ọ́.',
    'tone.expect.weak':
      'Ní ìwọ̀n awọ yìí àmì náà kò lágbára. Ori kì yóò lè ka ọ́ nígbà púpọ̀ — nígbà tí ó bá rí bẹ́ẹ̀, yóò sọ ọ́, dípò kí ó fún ọ ní nọ́mbà tí kò lè dúró lé.',
    'tone.expect.veryWeak':
      'Ní ìwọ̀n awọ yìí àmì náà kò lágbára rárá, Ori kì yóò sì lè ka ọ́ ní ọ̀pọ̀ ìgbà. Ìyẹn ni òtítọ́ nípa ohun tí kámẹ́rà lè rí lónìí. Gbogbo ìkọ̀ tí o bá pín ni ó ń yí i padà.',

    'shared.notKept':
      'A kò lè fi ìsọwọ́n yìí pamọ́ sínú ẹ̀rọ rẹ. Kò sí ohun tí a pín, ìsọwọ́n náà sì ti lọ — ó sábà máa ń jẹ́ ìtọ́jú àṣírí tàbí àyè ìpamọ́ tí ó ti kún.',

  },
};
